import React from "react";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useOnboarding } from "../../context/OnboardingContext";
import BrandHeart from "../qhearts/BrandHeart";

export default function StepShell({
  step,
  title,
  subtitle,
  children,
  onNext,
  nextLabel = "Weiter",
  nextDisabled = false,
  onSkip,
  skipLabel = "Überspringen",
  showBack = true,
  showProgress = true,
  footer,
}) {
  const { step: currentStep, back, STEP_COUNT } = useOnboarding();
  const actualStep = step ?? currentStep;
  const pct = Math.round((actualStep / STEP_COUNT) * 100);

  return (
    <div className="min-h-screen bg-white flex flex-col font-body text-qh-ink">
      {/* Top: logo + progress */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-qh-line">
        <div className="max-w-xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showBack && actualStep > 1 ? (
              <button
                onClick={back}
                className="h-10 w-10 rounded-full border border-qh-line flex items-center justify-center hover:bg-[#FFFBFB] transition-colors"
                aria-label="Zurück"
                data-testid="onboarding-back"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            ) : (
              <div className="h-10 w-10" />
            )}
            <Link to="/" className="flex items-center" aria-label="QHearts">
              <img
                src="/assets/qhearts-logo.png"
                alt="QHearts — Keep Dating Real"
                className="h-9 sm:h-10 w-auto object-contain select-none"
                draggable="false"
              />
            </Link>
          </div>
          {showProgress && (
            <span className="text-xs font-medium text-qh-slate tabular-nums" data-testid="onboarding-progress-label">
              {actualStep} / {STEP_COUNT}
            </span>
          )}
        </div>
        {showProgress && (
          <div className="h-1 bg-[#FFE4E6]">
            <motion.div
              className="h-full bg-qh-rose"
              initial={false}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 px-5 sm:px-8 py-8 sm:py-12">
        <motion.div
          key={actualStep}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="max-w-xl mx-auto"
        >
          {title && (
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight leading-[1.1]">
              {title}
            </h1>
          )}
          {subtitle && (
            <p className="mt-3 text-qh-slate leading-relaxed">{subtitle}</p>
          )}
          <div className="mt-8">{children}</div>
        </motion.div>
      </main>

      {/* Sticky bottom actions */}
      <footer className="sticky bottom-0 z-20 bg-white/95 backdrop-blur-xl border-t border-qh-line">
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-4 flex flex-col gap-2">
          {footer}
          {onNext && (
            <button
              onClick={onNext}
              disabled={nextDisabled}
              className="w-full h-12 rounded-full bg-qh-rose hover:bg-qh-rosedark text-white font-medium transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
              data-testid="onboarding-next"
            >
              {nextLabel}
            </button>
          )}
          {onSkip && (
            <button
              onClick={onSkip}
              className="w-full h-10 text-sm text-qh-slate hover:text-qh-ink underline underline-offset-2"
              data-testid="onboarding-skip"
            >
              {skipLabel}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
